import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import railGradient from '@/assets/images/questionnaire/journey-rail-gradient.webp';
import railNoise from '@/assets/svg/bg/questionnaire-noise.svg?url';
import { typographyVariantStyles } from '@/shared/design-system/typography-variants';

export type JourneyItemStatus = 'done' | 'current' | 'pending';

const CIRCLE_SIZE = 40;
const CONNECTOR_HEIGHT = 40;
const CURRENT_BORDER = 1.5;
const RAIL_CONTENT_WIDTH = 235;
const ENTRY_GAP = 36;
const LOGO_INSET = 24;
const INNER_RING_SIZE = 56;
const OUTER_RING_SIZE = 72;
const NOISE_TILE = 200;

export const StyledRail = styled('aside')(({ theme }) => ({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'hidden',
  borderRadius: theme.radius[24],
  backgroundColor: theme.colors.background.raised,
  backgroundImage: `url("${railNoise}"), url("${railGradient}")`,
  backgroundSize: `${NOISE_TILE}px ${NOISE_TILE}px, cover`,
  backgroundRepeat: 'repeat, no-repeat',
  backgroundPosition: '0 0, center bottom',
}));

export const StyledRailLogo = styled('div')({
  position: 'absolute',
  top: LOGO_INSET,
  left: LOGO_INSET,
  zIndex: 1,
  display: 'flex',
});

export const StyledRailContent = styled('div')(({ theme }) => ({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  width: RAIL_CONTENT_WIDTH,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing[64],
  paddingBlock: theme.spacing[24],
}));

export const StyledTimeline = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing[20],
}));

export const StyledDots = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingBlock: 2,
  flexShrink: 0,
});

export const StyledConnector = styled('span')({
  display: 'flex',
  height: CONNECTOR_HEIGHT,

  svg: {
    display: 'block',
    height: CONNECTOR_HEIGHT,
  },
});

export const StyledCircleSlot = styled('span')({
  position: 'relative',
  display: 'flex',
});

const OUTER_RING_TURN_MS = 16000;
const INNER_RING_TURN_MS = 10000;
const RING_BREATH_MS = 2400;

const turn = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const RING_DIMMED_OPACITY = 0.4;

const breathe = keyframes`
  from {
    opacity: ${RING_DIMMED_OPACITY};
  }
  to {
    opacity: 1;
  }
`;

const ring = (size: number, turnMs: number, direction: 'normal' | 'reverse') => ({
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  width: size,
  height: size,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none' as const,
  animation: `${turn} ${turnMs}ms linear infinite ${direction}, ${breathe} ${RING_BREATH_MS}ms ease-in-out infinite alternate`,

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export const StyledInnerRing = styled('img')(ring(INNER_RING_SIZE, INNER_RING_TURN_MS, 'reverse'));

export const StyledOuterRing = styled('img')(ring(OUTER_RING_SIZE, OUTER_RING_TURN_MS, 'normal'));

export const StyledCircle = styled('span', {
  shouldForwardProp: (prop) => prop !== '$status',
})<{ $status: JourneyItemStatus }>(({ theme, $status }) => ({
  position: 'relative',
  width: CIRCLE_SIZE,
  height: CIRCLE_SIZE,
  boxSizing: 'border-box',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.radius.full,
  ...typographyVariantStyles.body2,
  fontWeight: 500,
  color: {
    done: theme.colors.text.primary,
    current: theme.colors.text.brand,
    pending: theme.colors.text.tertiary,
  }[$status],
  backgroundColor: {
    done: theme.colors.brand.solid,
    current: 'transparent',
    pending: theme.colors.background.overlay,
  }[$status],
  border: $status === 'current' ? `${CURRENT_BORDER}px solid ${theme.colors.brand.solid}` : 'none',
}));

export const StyledEntries = styled('ol')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: ENTRY_GAP,
  whiteSpace: 'nowrap',
});

export const StyledEntry = styled('li')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

export const StyledQrCard = styled('div')(({ theme }) => ({
  position: 'relative',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing[12],
  padding: `${theme.spacing[24]}px ${theme.spacing[24]}px ${theme.spacing[12]}px`,
  borderRadius: `${theme.radius[16]}px ${theme.radius[16]}px 0 0`,
  backgroundColor: theme.colors.background.canvas,
  textAlign: 'center',
}));
